import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

export type EngineeringLoadingVariant = 'fullscreen' | 'contained' | 'compact';

export interface EngineeringLoadingScreenProps {
    /** Accessible status label (defaults to strings.LOADING). */
    ariaLabel?: string;
    variant?: EngineeringLoadingVariant;
    className?: string;
}

/**
 * Shared engineering-style loading animation (frosted overlay + mechanical elements).
 */
export function EngineeringLoadingScreen({
    ariaLabel,
    variant = 'fullscreen',
    className = '',
}: EngineeringLoadingScreenProps) {
    const { strings } = useLanguage();
    const label = ariaLabel ?? strings.LOADING;
    const rootClass = [
        'app-engineering-loading-overlay',
        variant === 'fullscreen' && 'app-engineering-loading-overlay--fullscreen',
        variant === 'contained' && 'app-engineering-loading-overlay--contained',
        variant === 'compact' && 'app-engineering-loading-overlay--compact',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rootClass} role="status" aria-busy="true" aria-label={label}>
            <div className="pdf-save-engineering-loader" aria-hidden>
                <div className="pdf-save-eng-scanline" />
                <div className="pdf-save-eng-frame">
                    <span className="pdf-save-eng-corner pdf-save-eng-corner--tl" />
                    <span className="pdf-save-eng-corner pdf-save-eng-corner--tr" />
                    <span className="pdf-save-eng-corner pdf-save-eng-corner--bl" />
                    <span className="pdf-save-eng-corner pdf-save-eng-corner--br" />
                </div>
                <div className="pdf-save-eng-stage">
                    <div className="pdf-save-eng-caliper">
                        <div className="pdf-save-eng-caliper-bar" />
                        <div className="pdf-save-eng-caliper-jaw pdf-save-eng-caliper-jaw--left" />
                        <div className="pdf-save-eng-caliper-jaw pdf-save-eng-caliper-jaw--right" />
                    </div>
                    <div className="pdf-save-eng-gears">
                        <svg className="pdf-save-eng-gear-svg" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
                            <g className="pdf-save-eng-gear-spin pdf-save-eng-gear-spin--cw">
                                <circle cx="28" cy="28" r="10" className="pdf-save-eng-gear-hub" />
                                <g className="pdf-save-eng-gear-spokes" strokeLinecap="round">
                                    <line x1="28" y1="4" x2="28" y2="14" />
                                    <line x1="28" y1="42" x2="28" y2="52" />
                                    <line x1="4" y1="28" x2="14" y2="28" />
                                    <line x1="42" y1="28" x2="52" y2="28" />
                                    <line x1="11" y1="11" x2="17" y2="17" />
                                    <line x1="39" y1="39" x2="45" y2="45" />
                                    <line x1="45" y1="11" x2="39" y2="17" />
                                    <line x1="17" y1="39" x2="11" y2="45" />
                                </g>
                            </g>
                        </svg>
                        <svg
                            className="pdf-save-eng-gear-svg pdf-save-eng-gear-svg--secondary"
                            viewBox="0 0 56 56"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g className="pdf-save-eng-gear-spin pdf-save-eng-gear-spin--ccw">
                                <circle cx="28" cy="28" r="7" className="pdf-save-eng-gear-hub" />
                                <g className="pdf-save-eng-gear-spokes pdf-save-eng-gear-spokes--six" strokeLinecap="round">
                                    <line x1="28" y1="6" x2="28" y2="13" />
                                    <line x1="28" y1="43" x2="28" y2="50" />
                                    <line x1="6" y1="28" x2="13" y2="28" />
                                    <line x1="43" y1="28" x2="50" y2="28" />
                                    <line x1="13.5" y1="13.5" x2="18" y2="18" />
                                    <line x1="38" y1="38" x2="42.5" y2="42.5" />
                                </g>
                            </g>
                        </svg>
                    </div>
                    <div className="pdf-save-eng-scale">
                        <div className="pdf-save-eng-scale-track" />
                        <div className="pdf-save-eng-scale-cursor" />
                    </div>
                </div>
                <div className="pdf-save-eng-reticle">
                    <span className="pdf-save-eng-reticle-ring" />
                    <span className="pdf-save-eng-reticle-cross" />
                </div>
            </div>
        </div>
    );
}
